import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { authService } from '../services/auth';
import creditService from '../services/credits';
import logger from '../utils/logger';

const AuthContext = createContext();

const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.access_token,
        loading: false,
        error: null,
      };
    case 'LOGIN_ERROR':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
        error: null,
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
      };
    case 'CREDITS_UPDATE':
      return {
        ...state,
        user: state.user ? {
          ...state.user,
          credit_balance: action.payload.balance,
          monthly_credits: action.payload.monthly_credits,
          has_unlimited_credits: action.payload.is_unlimited,
        } : state.user,
        lastCreditsUpdate: action.payload.timestamp,
      };
    default:
      return state;
  }
};

const initialState = {
  isAuthenticated: false,
  user: null,
  token: null,
  loading: true,
  error: null,
  lastCreditsUpdate: null,
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    // Check if user is logged in on app start
    const checkAuth = () => {
      const token = localStorage.getItem('access_token');
      const user = authService.getUser();

      if (token && user) {
        dispatch({
          type: 'SET_USER',
          payload: user,
        });
      }

      dispatch({ type: 'SET_LOADING', payload: false });
    };

    checkAuth();
  }, []);

  // Refresh token proactively every 6 hours (before the 1-week expiration)
  useEffect(() => {
    let refreshInterval;

    if (state.isAuthenticated) {
      refreshInterval = setInterval(async () => {
        try {
          logger.log('Proactive token refresh...');
          await authService.refreshToken();
          logger.log('Token refreshed successfully');
        } catch (error) {
          // Only logout on authentication errors (401/403), not network or server errors.
          // Transient failures (network glitch, server restart) should not end the session.
          const status = error.response?.status;
          if (status === 401 || status === 403) {
            logger.error('Proactive token refresh failed - auth error, logging out:', error);
            dispatch({ type: 'LOGOUT' });
          } else {
            logger.warn('Proactive token refresh failed - transient error, will retry later:', error.message);
          }
        }
      }, 6 * 60 * 60 * 1000); // 6 hours
    }

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [state.isAuthenticated]);

  // Validate session every 15 minutes
  useEffect(() => {
    let validationInterval;

    if (state.isAuthenticated) {
      validationInterval = setInterval(async () => {
        try {
          logger.log('Validating session...');
          const user = await authService.getCurrentUser();
          logger.log('Session valid');
          // Update user data (plan info, project counts, etc.)
          dispatch({ type: 'SET_USER', payload: user });
        } catch (error) {
          // Only logout on authentication errors (401/403), not network or server errors.
          // Transient failures (network glitch, server restart) should not end the session.
          const status = error.response?.status;
          if (status === 401 || status === 403) {
            logger.error('Session validation failed - auth error, logging out:', error);
            dispatch({ type: 'LOGOUT' });
            window.location.href = '/auth/login';
          } else {
            logger.warn('Session validation failed - transient error, will retry later:', error.message);
          }
        }
      }, 15 * 60 * 1000); // 15 minutes
    }

    return () => {
      if (validationInterval) {
        clearInterval(validationInterval);
      }
    };
  }, [state.isAuthenticated]);

  // Poll credit balance every 5 minutes while authenticated
  const fetchCredits = useCallback(async () => {
    try {
      const response = await creditService.getBalance();
      if (response?.success && response?.data) {
        dispatch({
          type: 'CREDITS_UPDATE',
          payload: {
            balance: response.data.balance,
            monthly_credits: response.data.monthly_credits,
            is_unlimited: response.data.is_unlimited,
            timestamp: Date.now(),
          },
        });
      }
    } catch (error) {
      const status = error.response?.status;
      if (status !== 401 && status !== 403) {
        logger.warn('Credits polling failed - transient error:', error.message);
      }
    }
  }, []);

  useEffect(() => {
    let creditsInterval;

    if (state.isAuthenticated) {
      // Fetch immediately on auth, then every 5 minutes
      fetchCredits();
      creditsInterval = setInterval(fetchCredits, 5 * 60 * 1000); // 5 minutes
    }

    return () => {
      if (creditsInterval) {
        clearInterval(creditsInterval);
      }
    };
  }, [state.isAuthenticated, fetchCredits]);

  const login = async (email, password) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const data = await authService.login(email, password);
      dispatch({ type: 'LOGIN_SUCCESS', payload: data });
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      dispatch({ type: 'LOGIN_ERROR', payload: message });
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      dispatch({ type: 'LOGOUT' });
    } catch (error) {
      logger.error('Logout error:', error);
      dispatch({ type: 'LOGOUT' });
    }
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const refreshUser = async () => {
    try {
      const user = await authService.getCurrentUser();
      dispatch({ type: 'SET_USER', payload: user });
      return user;
    } catch (error) {
      logger.error('Error refreshing user data:', error);
    }
  };

  const value = {
    ...state,
    login,
    logout,
    clearError,
    refreshUser,
    refreshCredits: fetchCredits,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};